import { z } from 'zod'

/**
 * fetchJson — fetch a URL, parse, and validate with a Zod schema.
 *
 * Throws on:
 * - Non-2xx responses (includes URL in message)
 * - JSON parse failure
 * - Schema validation failure
 */
export async function fetchJson<T>(
  url: string,
  schema: z.ZodType<T>
): Promise<T> {
  if (!url) {
    throw new Error(`fetchJson: empty URL`)
  }

  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    cache: 'no-cache',
  })

  if (!res.ok) {
    throw new Error(`fetchJson: ${res.status} ${res.statusText} — ${url}`)
  }

  let json: unknown
  try {
    json = await res.json()
  } catch (err) {
    throw new Error(`fetchJson: JSON parse failed — ${url}: ${String(err)}`)
  }

  const parsed = schema.safeParse(json)
  if (!parsed.success) {
    throw new Error(
      `fetchJson: schema validation failed — ${url}: ${parsed.error.message}`
    )
  }

  return parsed.data
}

/**
 * A bare fetch-only variant that returns unknown JSON without schema validation.
 * Use only when schema is not yet defined.
 */
export async function fetchRaw(url: string): Promise<unknown> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) {
    throw new Error(`fetchRaw: ${res.status} ${res.statusText} — ${url}`)
  }
  return res.json()
}
